import React, { createContext, useState, useContext, useEffect } from 'react';
import {
  getToken,
  setToken,
  clearSession,
  validateToken,
  getCurrentUser,
  extractTokenFromUrl,
  extractRegionFromUrl,
  cleanUrlAfterTokenExtraction,
  verifySession,
  hasSession,
  logout as authLogout,
} from '../services/auth';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Initialize authentication on app load
   */
  useEffect(() => {
    const initAuth = async () => {
      setLoading(true);
      setError(null);

      // AUTH BYPASS: Skip authentication if disabled via env variable or on localhost
      // Set REACT_APP_DISABLE_AUTH=true in Railway to bypass authentication
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      if (process.env.REACT_APP_DISABLE_AUTH === 'true' || process.env.NODE_ENV === 'development' || isLocalhost) {
        console.log('🔓 DEV MODE: Auth bypassed for testing');
        setUser({
          id: 'bypass-user',
          email: 'admin@lance.com',
          name: 'Lance Admin',
          role: 'admin',
          subscriptions: { 'FINMC': ['WKO', 'SBI', 'OPE', 'INCV', 'INC', 'TWO', 'TWOV', 'WKOV', 'UIT', '3RT', 'AOP', 'AVP', 'DSC', 'DVF', 'GEX', 'GEXV'] }
        });
        setIsAuthenticated(true);
        setLoading(false);
        return;
      }

      // Step 1: Check if token is in URL
      const urlToken = extractTokenFromUrl(window.location.search);
      const region = extractRegionFromUrl(window.location.search);

      if (urlToken) {
        console.log('Token found in URL, exchanging for session...');

        // IMMEDIATELY clean URL to remove sensitive token from browser bar
        // Do this before async exchange so token is never visible for long
        cleanUrlAfterTokenExtraction();

        // Exchange the token for a session (new flow)
        const result = await validateToken(urlToken);

        if (result.valid) {
          setUser(result.user);
          setIsAuthenticated(true);
          console.log('Token exchanged and session created');
        } else {
          // Invalid token from URL
          console.error('Invalid token from URL:', result.error);
          setError(result.error);
          setIsAuthenticated(false);
          clearSession();
        }

        setLoading(false);
        return;
      }

      // Step 2: Check if we have an existing session
      if (hasSession()) {
        console.log('Session found, verifying...');

        // Verify the session with backend
        const result = await verifySession();

        if (result.valid) {
          setUser(result.user);
          setIsAuthenticated(true);
          console.log('Session is valid');
        } else {
          // Session is invalid/expired
          console.error('Session invalid:', result.error);
          setError(result.error);
          setIsAuthenticated(false);
          clearSession();
        }

        setLoading(false);
        return;
      }

      // Step 3: Check if token exists in localStorage (legacy)
      const storedToken = getToken();

      if (storedToken) {
        console.log('Token found in localStorage, validating...');

        // Validate stored token
        const result = await validateToken(storedToken);

        if (result.valid) {
          setUser(result.user);
          setIsAuthenticated(true);
          console.log('Stored token is valid');
        } else {
          // Stored token is invalid/expired
          console.error('Stored token invalid:', result.error);
          setError(result.error);
          setIsAuthenticated(false);
          clearSession();
        }
      } else {
        // No token found anywhere
        console.log('No token found');
        setIsAuthenticated(false);
        setError('No authentication token found');
      }

      setLoading(false);
    };

    initAuth();
  }, []);

  /**
   * Logout user
   */
  const logout = async () => {
    await authLogout();
    setUser(null);
    setIsAuthenticated(false);
    setError('Logged out');
  };

  /**
   * Refresh user data
   */
  const refreshUser = async () => {
    const result = await verifySession();
    if (result.valid) {
      setUser(result.user);
    } else {
      await logout();
    }
  };

  const value = {
    isAuthenticated,
    user,
    loading,
    error,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
